'use client'
import Image from 'next/image'
import PayPalLogo from '@betterplace/assets/images/logos/paypal.svg'
import React, { type ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import analytics from '@betterplace/product-analytics'
import styles from './PaypalButton.module.css'
import useIsFormValid from '../useIsFormValid'
import { ActionFormSubmitErrorTypes } from '@/form/ActionForm/useActionForm/useActionForm'
import {
  DISPATCH_ACTION,
  PayPalButtons,
  PayPalButtonsComponentProps,
  PayPalScriptProvider,
  ReactPayPalScriptOptions,
  SCRIPT_LOADING_STATE,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js'
import { Icon } from '@betterplace/design-system/client'
import { report } from '@/errorReporting'
import {
  useDonationFormConfig,
  useDonationFormContext,
  useRedirectHandler,
} from '@/donationPages/_dependencies/helpers'
import { useIsMounted, useNetworkStatus } from '@/helpers/hooks'
import { useLocale, useTranslations } from 'next-intl'
import { useMessageContext } from '@/components/Messages'
import type { Maybe } from '@/types'

function useFormSubmit(): {
  redirectToRef: React.MutableRefObject<string | undefined>
  createBillingAgreement: () => Promise<string>
  donationIdRef: React.MutableRefObject<string | undefined>
} {
  const t = useTranslations()
  const redirectToRef = useRef<string | undefined>(undefined)
  const donationIdRef = useRef<string | undefined>(undefined)
  const { addMessage } = useMessageContext()
  const { submit, setBusy } = useDonationFormContext()

  const createBillingAgreement = useCallback(async (): Promise<string> => {
    redirectToRef.current = undefined
    setBusy(true)
    try {
      const {
        data: { redirectTo, donationId, paypalToken },
      } = await submit()
      if (!paypalToken) throw new Error('No paypal token received')
      if (!redirectTo) throw new Error('No redirect url received')
      if (!donationId) throw new Error('No donation ID received')

      donationIdRef.current = donationId
      redirectToRef.current = redirectTo
      return paypalToken
    } catch (e_) {
      const e = e_ as Maybe<Error>
      if (e?.message === ActionFormSubmitErrorTypes.ValidationError) {
        throw e
      }
      addMessage({ message: t('nextjs.errors.messages.submit'), type: 'error' })
      report(e)
      setBusy(false)
      return ''
    }
  }, [addMessage, setBusy, submit, t])

  return { createBillingAgreement, redirectToRef, donationIdRef }
}

function usePaypalOptions(clientId?: string) {
  const locale = useLocale()
  const [options, setOptions] = useState<ReactPayPalScriptOptions>({
    clientId: '',
    commit: true,
    currency: 'EUR',
    intent: 'tokenize',
    disableFunding: 'card,sepa',
    vault: true,
    locale: locale === 'de' ? 'de_DE' : 'en_US',
    dataReactPaypalScriptId: 'sdk-paypal-script',
    dataNamespace: 'sdk-paypal', // solves the issue with paypal namespace conflicting with input#paypal
    components: 'buttons',
  })
  useEffect(() => {
    setOptions((old) => ({ ...old, clientId: clientId ?? '' }))
  }, [clientId])
  return options.clientId ? options : undefined
}

function usePayPalButtonProps(): PayPalButtonsComponentProps & { error?: PayPalFatalError } {
  const isValid = useIsFormValid()
  const { debug } = useDonationFormConfig()
  const { setBusy, formState, getValues } = useDonationFormContext()
  const { isSubmitting } = formState
  const redirectHandler = useRedirectHandler()
  const { createBillingAgreement, redirectToRef, donationIdRef } = useFormSubmit()
  const onApprove = useCallback(async () => {
    if (redirectToRef.current && donationIdRef.current) {
      await redirectHandler(redirectToRef.current, donationIdRef.current, getValues())
    }
  }, [redirectHandler, redirectToRef, donationIdRef, getValues])

  const onCancel = useCallback(() => {
    setBusy(false)
  }, [setBusy])

  const [error, setError] = useState<PayPalFatalError | undefined>(undefined)

  const onError = useCallback(
    (e_: unknown) => {
      setBusy(false)
      const e = e_ as Maybe<Error>
      if (e?.message === ActionFormSubmitErrorTypes.ValidationError) {
        return
      }
      setError(PayPalFatalError.Other)
    },
    [setBusy, setError]
  )

  const env = useMemo(() => (debug ? 'sandbox' : 'production'), [debug])

  const props = useMemo(
    () => ({
      onCancel,
      onError,
      isSubmitting,
      createBillingAgreement,
      onApprove,
      error,
      env,
      style: buttonStyle,
      disabled: !isValid,
    }),
    [onCancel, onError, isSubmitting, createBillingAgreement, onApprove, error, env, isValid]
  )
  return props
}

const PaypalButton = memo(function PaypalButton({ clientId, onClick }: { clientId?: string; onClick?: () => void }) {
  const options = usePaypalOptions(clientId)
  if (!clientId) return <OtherErrorMessage />
  if (!options) return <DummyPayPalButtonWrapper busy />
  return (
    <PayPalScriptProvider deferLoading options={options}>
      <PayPalButtonInner onClick={onClick} />
    </PayPalScriptProvider>
  )
})

const PayPalButtonStatus = {
  Initial: 'Initial',
  ReadyToLoad: 'ReadyToLoad',
  FieldError: 'FieldError',
  FatalError: 'FatalError',
  PaypalError: 'PaypalError',
  SDKLoadingError: 'SDKLoadingError',
  Ok: 'Ok',
  Busy: 'Busy',
  Offline: 'Offline',
} as const

type PayPalButtonStatus = (typeof PayPalButtonStatus)[keyof typeof PayPalButtonStatus]

const PayPalFatalError = {
  LoadingFailed: 'LoadingFailed',
  TooManyRequests: 'TooManyRequests',
  UnsupportedBrowser: 'UnsupportedBrowser',
  SafariTimeout: 'SafariTimeout',
  Other: 'Other',
} as const

type PayPalFatalError = (typeof PayPalFatalError)[keyof typeof PayPalFatalError]

function useTimeoutError() {
  const [{ isPending, isInitial, isResolved, isRejected }] = usePayPalScriptReducer()
  const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent)
  const [hasTimeoutError, setHasTimeoutError] = useState<boolean>(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isPendingRef = useRef<boolean>(isPending && !(isResolved || isRejected))
  useEffect(() => {
    isPendingRef.current = isPending && !(isResolved || isRejected)
  }, [isPending, isInitial, isResolved, isRejected])
  useEffect(() => {
    if (!isSafari) return
    timeoutRef.current = setTimeout(() => {
      if (!isPendingRef.current) return
      analytics.donationSafariPaypalLoadingError({
        navigator: navigator.userAgent,
        os: navigator.platform,
      })
      setHasTimeoutError(true)
    }, 15000)
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isSafari])
  return hasTimeoutError
}

function usePayPalStatus(props: Pick<ReturnType<typeof usePayPalButtonProps>, 'error'> | undefined) {
  const [{ isResolved, isRejected, isInitial, isPending }] = usePayPalScriptReducer()
  const { formState } = useDonationFormContext()
  const { isDirty, isSubmitting } = formState
  const networkStatus = useNetworkStatus()
  const isValid = useIsFormValid()
  const isOffline = networkStatus === 'offline'
  const isUnsupportedBrowser = /Twitter/i.test(navigator.userAgent)
  const hasSafariTimeoutError = useTimeoutError()
  const result = useMemo(() => {
    if (!props) return [PayPalButtonStatus.Initial, undefined] as const
    if (props.error) return [PayPalButtonStatus.FatalError, props.error] as const
    if (isUnsupportedBrowser) return [PayPalButtonStatus.FatalError, PayPalFatalError.UnsupportedBrowser] as const
    if (isInitial && isValid && isDirty) return [PayPalButtonStatus.ReadyToLoad, undefined] as const
    if (isInitial) return [PayPalButtonStatus.Initial, undefined] as const
    if (hasSafariTimeoutError) return [PayPalButtonStatus.SDKLoadingError, PayPalFatalError.SafariTimeout] as const
    if (isPending || isSubmitting) return [PayPalButtonStatus.Busy, undefined] as const
    if (!isValid) return [PayPalButtonStatus.FieldError, undefined] as const
    if (isOffline) return [PayPalButtonStatus.Offline, undefined] as const
    if (isRejected) return [PayPalButtonStatus.FatalError, PayPalFatalError.LoadingFailed] as const
    if (isResolved) return [PayPalButtonStatus.Ok, undefined] as const
    return [PayPalButtonStatus.Busy, undefined] as const
  }, [
    props,
    isUnsupportedBrowser,
    isInitial,
    isValid,
    isDirty,
    isPending,
    hasSafariTimeoutError,
    isSubmitting,
    isOffline,
    isRejected,
    isResolved,
  ])
  return result
}

function useLoadPayPalWhenReady(status: PayPalButtonStatus) {
  const [_, dispatch] = usePayPalScriptReducer()
  useEffect(() => {
    if (status !== PayPalButtonStatus.ReadyToLoad) return
    dispatch({ type: DISPATCH_ACTION.LOADING_STATUS, value: SCRIPT_LOADING_STATE.PENDING })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])
}

function DummyPayPalButtonWrapper({
  onClick,
  children,
  busy,
}: {
  onClick?: () => void
  children?: ReactNode
  busy?: boolean
}) {
  return (
    <div className={styles.container} style={{ height: buttonHeight }}>
      <DummyPayPalButton busy={busy} onClick={onClick} />
      {children}
    </div>
  )
}

function PayPalButtonInner({ onClick }: { onClick?: () => void }) {
  const { error, ...props } = usePayPalButtonProps()
  const [status, fatalError] = usePayPalStatus({ error })
  useLoadPayPalWhenReady(status)
  if (status === PayPalButtonStatus.FatalError) return <FatalErrorMessage error={fatalError} />
  const busy = status === PayPalButtonStatus.Busy
  const ok = status === PayPalButtonStatus.Ok
  return (
    <>
      {status === PayPalButtonStatus.SDKLoadingError && <FatalErrorMessage error={fatalError} />}
      <DummyPayPalButtonWrapper onClick={onClick} busy={busy}>
        <div style={!ok ? { display: 'none' } : {}}>
          <PayPalButtons onClick={onClick} {...props} />
        </div>
      </DummyPayPalButtonWrapper>
    </>
  )
}

function DummyPayPalButton({ busy, onClick: onClick_ }: { busy?: boolean; onClick?: () => void }) {
  const t = useTranslations()
  const isValid = useIsFormValid()
  const disabled = !useIsMounted()
  const onClick = useCallback(() => {
    if (busy || !isValid) return
    onClick_?.()
  }, [onClick_, busy, isValid])

  return (
    <div style={dummyButtonStyle}>
      <button
        style={{ height: buttonHeight, backgroundColor: 'transparent' }}
        className={styles.dummyPaypalButton}
        onClick={onClick}
        disabled={disabled || busy}
        type="submit"
      >
        {busy && <Icon color="bg-light" name="spinner" size="400" />}
        <Image style={{ display: 'inline-block' }} src={PayPalLogo as string} alt={t('nextjs.core.submit')} />
      </button>
    </div>
  )
}

function FatalErrorMessage({ error }: { error?: PayPalFatalError }) {
  if (typeof error === 'undefined') return null
  switch (error) {
    case PayPalFatalError.TooManyRequests:
      return <Error429Message />
    case PayPalFatalError.LoadingFailed:
      return <NoPaypalJsMessage />
    case PayPalFatalError.UnsupportedBrowser:
      return <UnsupportedBrowserMessage />
    case PayPalFatalError.SafariTimeout:
      return <SafariTimeoutMessage />
    default:
      return <OtherErrorMessage />
  }
}

function NoPaypalJsMessage() {
  const t = useTranslations()
  return <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_ad_blocker')}</p>
}

function Error429Message() {
  const t = useTranslations()
  return (
    <>
      <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_too_many_requests')}</p>
      <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_too_many_requests_settings')}</p>
    </>
  )
}

function OtherErrorMessage() {
  const t = useTranslations()
  return <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_other')}</p>
}

function UnsupportedBrowserMessage() {
  const t = useTranslations()
  return <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_unsupported_browser')}</p>
}

function SafariTimeoutMessage() {
  const t = useTranslations()
  return <p className={styles.errorMessage}>{t('nextjs.donate.errors.paypal_safari_timeout')}</p>
}

const buttonHeight = 50

const buttonStyle: PayPalButtonsComponentProps['style'] = {
  height: buttonHeight,
  color: 'gold',
  fundingicons: false, // does not exist on props thus casting below
  label: 'checkout',
  shape: 'rect',
  size: 'responsive',
  tagline: false,
} as PayPalButtonsComponentProps['style']

const dummyButtonStyle: React.CSSProperties = {
  height: buttonHeight,
  margin: 0,
  textAlign: 'center',
  zIndex: -1,
  position: 'absolute',
  left: 0,
  right: 0,
}
export default PaypalButton
